import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@topcon/topcon-utils';
import {
  TeButton,
  TeButtonMinimal,
  TeContextMenu,
  TeContextMenuControl,
  TeContextMenuItem,
} from '@topcon/tuicl-react';

import PageLayout from '../../components/Page/PageLayout';
import EmptyState from '../../components/ServiceAgreement/Common/EmptyState';
import CreateServiceAgreementCustomerModal from '../../components/ServiceAgreement/Customer/CreateServiceAgreementCustomerModal';
import CustomerMigrationCallout from '../../components/ServiceAgreement/Customer/CustomerMigrationCallout';
import TerminateCancelServiceAgreementModal from '../../components/ServiceAgreement/Customer/TerminateCancelServiceAgreementModal';
import {
  navigateToCustomerSupportAuthURL,
  navigateToReviewPage,
  ReviewSource,
} from '../../components/ServiceAgreement/serviceAgreementReviewNavigation';
import { ServiceAgreementBody } from '../../api/generated/pm';
import { useAppService } from '../../providers/app/app.context';
import { UserRole } from '../../types';
import AddressCell from './common/cells/AddressCell';
import BusinessPartnerIconCell from './common/cells/BusinessPartnerIconCell';
import CellText from './common/cells/CellText';
import CountryCell from './common/cells/CountryCell';
import DivisionCell from './common/cells/DivisionCell';
import OrganizationNumberCell from './common/cells/OrganizationNumberCell';
import ServiceTypeCell from './common/cells/ServiceTypeCell';
import { UNKNOWN_VALUE } from './common/constants';
import DivisionFilterButton from './common/DivisionFilterButton';
import DivisionFilterDrawer from './common/DivisionFilterDrawer';
import DivisionQuickFilter from './common/DivisionQuickFilter';
import ServerPagedTable, { ServerPagedTableColumn } from './common/ServerPagedTable';
import { useDivisionRollup } from '../../hooks/queries/useDivisionRollup';
import { useListError } from '../../hooks/useListError';
import { useListState } from '../../hooks/useListState';
import { useVisibilityRefetch } from '../../hooks/useVisibilityRefetch';
import { useCustomersList } from './customers.queries';

/**
 * Customers (ticket 02, CSSD-1646): server-paged table of the Dealer's active
 * Service Agreements, with the Division quick filter, Division column and
 * Division filter drawer for a Dealer with Divisions (PRD D5/D6). Replaces
 * the v1 `ServiceAgreementsList type="customer"` page.
 */
function Customers() {
  const { t } = useTranslation();
  const { userRoles, loadedRoles } = useAppService();
  const divisionRollup = useDivisionRollup();
  const {
    isReady,
    hasDivisions,
    divisions,
    scope,
    setScope,
    selectedDivisionIds,
    applyDivisions,
    clearDivisions,
    resolveDivisionName,
    isOwnRow,
    filterKey,
  } = divisionRollup;

  const { page, setPage, limit, setLimit, searchInput, setSearchInput, search } = useListState({
    resetKey: filterKey,
  });

  const query = useCustomersList({
    page,
    limit,
    search,
    scope,
    selectedDivisionIds,
    enabled: isReady,
  });

  useVisibilityRefetch(query.refetch);
  const isError = useListError(query.error);

  const hasSupportRole =
    loadedRoles && userRoles.some((role) => role === UserRole.ServiceCustomerAdministrator);

  const [isAddCustomerOpen, setAddCustomerOpen] = useState(false);
  const [isFiltersOpen, setFiltersOpen] = useState(false);
  const [terminateTarget, setTerminateTarget] = useState<ServiceAgreementBody | null>(null);

  const handleReview = useCallback((sa: ServiceAgreementBody) => {
    navigateToReviewPage(sa.id, ReviewSource.Customers);
  }, []);

  const handleSupportCustomer = useCallback((sa: ServiceAgreementBody) => {
    navigateToCustomerSupportAuthURL(sa.id, sa.customerBusinessPartnerId);
  }, []);

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  const columns = useMemo<ServerPagedTableColumn<ServiceAgreementBody>[]>(() => {
    const cols: ServerPagedTableColumn<ServiceAgreementBody>[] = [
      {
        key: 'icon',
        header: '',
        compact: true,
        render: () => <BusinessPartnerIconCell />,
      },
      {
        key: 'customer',
        header: t('customers.columnCustomerName'),
        minWidth: 200,
        render: (sa) => <CellText text={sa.customer?.name ?? UNKNOWN_VALUE} />,
      },
    ];

    if (hasDivisions) {
      cols.push({
        key: 'division',
        header: t('customersTable.columnDivision'),
        minWidth: 160,
        render: (sa) => <DivisionCell name={resolveDivisionName(sa.providerBusinessPartnerId)} />,
      });
    }

    cols.push(
      {
        key: 'serviceType',
        header: t('customersTable.columnServiceType'),
        render: (sa) => <ServiceTypeCell agreementTerms={sa.serviceAgreementTerms} />,
      },
      {
        key: 'activeSince',
        header: t('customers.columnActiveSince'),
        justifyContent: 'flex-end',
        render: (sa) => (
          <CellText
            text={(sa.activeSince && formatDate(new Date(sa.activeSince))) || UNKNOWN_VALUE}
          />
        ),
      },
      {
        key: 'organizationNumber',
        header: t('customersTable.columnOrganizationNumber'),
        headerInfoTooltip: t('customersTable.tooltipOrganizationNumber'),
        justifyContent: 'flex-end',
        render: (sa) => <OrganizationNumberCell extId={sa.customer?.extId} />,
      },
      {
        key: 'customerNumber',
        header: t('customers.columnCustomerNumber'),
        headerInfoTooltip: t('customersTable.tooltipCustomerNumber'),
        justifyContent: 'flex-end',
        render: (sa) => <CellText text={sa.providerCustomerNumber ?? UNKNOWN_VALUE} />,
      },
      {
        key: 'address',
        header: t('customersTable.columnCompanyAddress'),
        minWidth: 240,
        render: (sa) => <AddressCell address={sa.customer?.companyAddress} />,
      },
      {
        key: 'country',
        header: t('customersTable.columnCountry'),
        minWidth: 120,
        render: (sa) => <CountryCell countryCode={sa.customer?.companyAddress?.country} />,
      },
      {
        key: 'actions',
        header: '',
        compact: true,
        justifyContent: 'flex-end',
        // Division rows are read-only and have no ⋮ menu (PRD D13).
        render: (sa) => {
          if (!isOwnRow(sa.providerBusinessPartnerId)) return null;
          const contextId = `customers-row-${sa.id}`;
          return (
            <>
              <TeContextMenuControl contextId={contextId}>
                <TeButtonMinimal icon="more_vert" variant="secondary" size="s" />
              </TeContextMenuControl>
              <TeContextMenu contextId={contextId} size="s">
                <TeContextMenuItem text={t('reviewAgreement')} teclick={() => handleReview(sa)} />
                {hasSupportRole && (
                  <TeContextMenuItem
                    text={t('supportCustomer')}
                    teclick={() => handleSupportCustomer(sa)}
                  />
                )}
                <TeContextMenuItem
                  text={t('terminateAgreement')}
                  textColor="text.error.primary"
                  teclick={() => setTerminateTarget(sa)}
                />
              </TeContextMenu>
            </>
          );
        },
      }
    );

    return cols;
  }, [
    handleReview,
    handleSupportCustomer,
    hasDivisions,
    hasSupportRole,
    isOwnRow,
    resolveDivisionName,
    t,
  ]);

  return (
    <PageLayout
      fullWidth
      title={t('customers.title')}
      itemCount={query.isError ? undefined : total}
      leftAccessory={
        hasDivisions ? <DivisionQuickFilter scope={scope} onChange={setScope} /> : undefined
      }
      searchValue={searchInput}
      onSearchChange={setSearchInput}
      searchPlaceholder={t('customers.search')}
      actions={
        <>
          <TeButton
            variant="primary"
            text={t('customers.add')}
            teclick={() => setAddCustomerOpen(true)}
          />
          {hasDivisions && (
            <DivisionFilterButton
              isOpen={isFiltersOpen}
              hasActiveFilter={selectedDivisionIds.length > 0}
              onClick={() => setFiltersOpen((open) => !open)}
            />
          )}
        </>
      }
      drawer={
        hasDivisions ? (
          <DivisionFilterDrawer
            open={isFiltersOpen}
            onClose={() => setFiltersOpen(false)}
            divisions={divisions}
            selectedDivisionIds={selectedDivisionIds}
            onApply={applyDivisions}
            onClear={clearDivisions}
          />
        ) : undefined
      }
    >
      <CustomerMigrationCallout />
      <ServerPagedTable
        columns={columns}
        items={items}
        rowKey={(sa) => sa.id}
        total={total}
        page={page}
        limit={limit}
        onPageChange={setPage}
        onLimitChange={setLimit}
        // `isPending`, not `isLoading`: the query is disabled until Divisions
        // settle, and a disabled query without data is pending but not loading.
        isLoading={query.isPending}
        isFetching={query.isFetching}
        isError={isError}
        emptyState={<EmptyState kind={search ? 'noSearch' : 'noItems'} resource="customers" />}
      />

      {isAddCustomerOpen && (
        <CreateServiceAgreementCustomerModal
          open={isAddCustomerOpen}
          onClose={() => setAddCustomerOpen(false)}
        />
      )}

      {terminateTarget && (
        <TerminateCancelServiceAgreementModal
          serviceAgreement={terminateTarget}
          kind="agreement"
          open={terminateTarget !== null}
          onClose={() => setTerminateTarget(null)}
        />
      )}
    </PageLayout>
  );
}

export default Customers;
